import React, { useEffect, useRef, useState } from 'react';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Code,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';

import { sendTryItRequest, TryItResponse } from 'api/documentation';

import { ApiTryItResponse } from './ApiTryItResponse';
import { ApiOperation, ApiParameterValues } from './types';
import {
  missingParameters,
  operationRequestPath,
  TRY_IT_METHODS,
  unwrapSchema,
} from './utils';

interface Props {
  operation: ApiOperation;
  values: ApiParameterValues;
  onChange: (values: ApiParameterValues) => void;
}

/**
 * Sends the request as the signed-in user and shows the response. Read-only
 * methods only: other methods show their code samples instead.
 */
export const ApiTryIt = ({ operation, values, onChange }: Props) => {
  const [response, setResponse] = useState<TryItResponse>();
  const [error, setError] = useState<string>();
  const [sending, setSending] = useState(false);
  const controller = useRef<AbortController>();
  const inputs = operation.parameters.filter(
    parameter => parameter.in === 'path' || parameter.in === 'query',
  );

  // A new endpoint starts clean; leaving cancels a request in flight.
  useEffect(() => {
    setResponse(undefined);
    setError(undefined);
    return () => controller.current?.abort();
  }, [operation]);

  if (!TRY_IT_METHODS.includes(operation.method))
    return (
      <Text fontSize="15px" color="gray.600">
        Try it runs read-only requests only. Use the code samples above to call{' '}
        <Code fontSize="13px">{operation.method.toUpperCase()}</Code> endpoints
        from your scripts.
      </Text>
    );

  const send = async () => {
    const missing = missingParameters(operation, values);
    if (missing.length) {
      setError(`Enter a value for ${missing.join(', ')}.`);
      return;
    }
    let path: string;
    try {
      path = operationRequestPath(operation, values);
    } catch (pathError: any) {
      setError(pathError.message);
      return;
    }
    controller.current?.abort();
    controller.current = new AbortController();
    setError(undefined);
    setSending(true);
    try {
      setResponse(await sendTryItRequest(path, controller.current.signal));
    } catch (requestError: any) {
      if (requestError?.name !== 'CanceledError')
        setError('The request could not be sent. Check your connection.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" p={4}>
      <Text fontSize="14px" color="gray.600" mb={inputs.length ? 4 : 3}>
        Runs as you, with your access, against your organization's data.
      </Text>
      {inputs.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={4} spacingY={3}>
          {inputs.map(parameter => {
            const schema = unwrapSchema(parameter.schema);
            const value = values[parameter.name] ?? '';
            const update = (next: string) =>
              onChange({ ...values, [parameter.name]: next });
            const options =
              schema.enum?.map(String) ??
              (schema.type === 'boolean' ? ['true', 'false'] : undefined);
            return (
              <FormControl
                key={`${parameter.in}:${parameter.name}`}
                isRequired={parameter.required}
              >
                <FormLabel fontSize="13px" mb={1} fontFamily="mono">
                  {parameter.name}
                </FormLabel>
                {options ? (
                  <Select
                    size="sm"
                    borderRadius="md"
                    value={value}
                    onChange={event => update(event.target.value)}
                  >
                    <option value="">—</option>
                    {options.map(option => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <Input
                    size="sm"
                    borderRadius="md"
                    value={value}
                    placeholder={parameter.in === 'path' ? 'required' : ''}
                    onChange={event => update(event.target.value)}
                  />
                )}
              </FormControl>
            );
          })}
        </SimpleGrid>
      )}
      <Flex mt={4} gap={3} align="center">
        <Button
          size="sm"
          colorScheme="purple"
          bg="primary"
          isLoading={sending}
          onClick={send}
        >
          Send request
        </Button>
        {sending && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => controller.current?.abort()}
          >
            Cancel
          </Button>
        )}
      </Flex>
      {error && (
        <Alert status="warning" borderRadius="md" mt={4} py={2}>
          <AlertIcon />
          <AlertDescription fontSize="14px">{error}</AlertDescription>
        </Alert>
      )}
      {response && <ApiTryItResponse response={response} />}
    </Box>
  );
};
